<template>
    <footer class="ps-footer no_print" role="contentinfo">
        <!-- Top CTA -->
        <div class="ps-footer__cta">
            <div class="container">
                <div class="ps-footer__cta-inner">
                    <div class="ps-footer__cta-copy">
                        <p class="ps-footer__cta-label">{{ __('Ready to launch?') }}</p>
                        <h2 class="ps-footer__cta-title">{{ __('Start with Print Studio — it\'s free to explore') }}</h2>
                    </div>
                    <div class="ps-footer__cta-actions">
                        <Link :href="route('tools.show', 'print-studio')" class="ps-footer__cta-btn ps-footer__cta-btn--primary">
                            <icon name="print" class="w-4 h-4" />
                            {{ __('Open Print Studio') }}
                        </Link>
                        <Link :href="route('contact')" class="ps-footer__cta-btn ps-footer__cta-btn--ghost">
                            {{ __('Talk to us') }}
                        </Link>
                    </div>
                </div>
            </div>
        </div>

        <!-- Main grid -->
        <div class="ps-footer__main">
            <div class="container">
                <div class="ps-footer__grid">
                    <!-- Brand -->
                    <div class="ps-footer__brand">
                        <Link :href="route('home')" class="ps-footer__logo" aria-label="ProfileSuite home">
                            <logo name="white" class="ps-footer__logo-img" />
                        </Link>
                        <p class="ps-footer__tagline">{{ footerContent.tagline }}</p>
                        <div class="ps-footer__badges">
                            <span class="ps-footer__badge">
                                <icon name="security" class="w-3.5 h-3.5" />
                                {{ __('Self-hosted') }}
                            </span>
                            <span class="ps-footer__badge">
                                <icon name="print" class="w-3.5 h-3.5" />
                                {{ __('12+ tools') }}
                            </span>
                        </div>
                    </div>

                    <!-- Tools -->
                    <div class="ps-footer__col">
                        <h3 class="ps-footer__heading">{{ __('Creator tools') }}</h3>
                        <ul class="ps-footer__links">
                            <li v-for="tool in toolLinks" :key="tool.slug">
                                <Link :href="route('tools.show', tool.slug)" class="ps-footer__link">
                                    {{ tool.name }}
                                </Link>
                            </li>
                        </ul>
                    </div>

                    <!-- Company -->
                    <div class="ps-footer__col">
                        <h3 class="ps-footer__heading">{{ __('Company') }}</h3>
                        <ul class="ps-footer__links">
                            <li v-for="link in companyLinks" :key="link.id">
                                <Link :href="link.href" class="ps-footer__link">
                                    {{ __(link.label) }}
                                </Link>
                            </li>
                        </ul>
                    </div>

                    <!-- Newsletter -->
                    <div class="ps-footer__col ps-footer__col--newsletter">
                        <h3 class="ps-footer__heading">{{ __('Stay in the loop') }}</h3>
                        <p class="ps-footer__newsletter-text">
                            {{ __('Product updates, tips, and new tool releases.') }}
                        </p>
                        <form class="ps-footer__form" @submit.prevent="subscribe">
                            <label class="sr-only" for="footer-email">{{ __('Email address') }}</label>
                            <div class="ps-footer__input-wrap">
                                <icon name="email" class="ps-footer__input-icon w-4 h-4" />
                                <input
                                    id="footer-email"
                                    v-model="form.email"
                                    type="email"
                                    name="email"
                                    class="ps-footer__input"
                                    :placeholder="__('you@company.com')"
                                    required
                                    autocomplete="email"
                                />
                            </div>
                            <loading-button
                                :loading="form.processing"
                                type="submit"
                                class="ps-footer__submit"
                            >
                                {{ __('Subscribe') }}
                            </loading-button>
                        </form>
                    </div>
                </div>
            </div>
        </div>

        <!-- Bottom bar -->
        <div class="ps-footer__bottom">
            <div class="container">
                <div class="ps-footer__bottom-inner">
                    <p class="ps-footer__copyright" v-html="footerContent.copyright" />
                    <nav class="ps-footer__legal" aria-label="Legal">
                        <Link :href="route('privacy')" class="ps-footer__legal-link">{{ __('Privacy') }}</Link>
                        <span class="ps-footer__legal-dot" aria-hidden="true">·</span>
                        <Link :href="route('terms_service')" class="ps-footer__legal-link">{{ __('Terms') }}</Link>
                        <span class="ps-footer__legal-dot" aria-hidden="true">·</span>
                        <Link :href="route('faq')" class="ps-footer__legal-link">{{ __('FAQ') }}</Link>
                    </nav>
                </div>
            </div>
        </div>

        <div class="ps-footer__glow" aria-hidden="true" />
    </footer>
</template>

<script>
import Logo from '@/Shared/Logo'
import Icon from '@/Shared/Icon'
import LoadingButton from '@/Shared/LoadingButton'
import { Link } from '@inertiajs/vue3'

const DEFAULT_TAGLINE = 'Print business cards, digital vCards, bio links, landing pages, and lead capture — one self-hosted Laravel suite.'
const DEFAULT_COPYRIGHT = '© ProfileSuite — Developed by <a href="https://w3bd.com/" rel="noopener noreferrer">W3BD</a>.'

export default {
    components: {
        Logo,
        LoadingButton,
        Link,
        Icon,
    },
    props: {
        footer: Object,
    },
    data() {
        return {
            form: this.$inertia.form({
                email: '',
            }),
        }
    },
    computed: {
        footerContent() {
            if (!this.footer?.html) {
                return {
                    tagline: DEFAULT_TAGLINE,
                    copyright: DEFAULT_COPYRIGHT,
                }
            }

            let html = this.footer.html
            if (typeof html === 'string') {
                try {
                    html = JSON.parse(html)
                } catch {
                    return {
                        tagline: DEFAULT_TAGLINE,
                        copyright: DEFAULT_COPYRIGHT,
                    }
                }
            }

            return {
                tagline: html.text || html.footer_text || DEFAULT_TAGLINE,
                copyright: html.copyright || html.footer_copyright || DEFAULT_COPYRIGHT,
            }
        },
        toolLinks() {
            return [
                { slug: 'print-studio', name: 'Print Studio' },
                { slug: 'business-card', name: 'Business Card' },
                { slug: 'vcard', name: 'vCard Generator' },
                { slug: 'link-in-bio', name: 'Link-in-Bio' },
                { slug: 'landing-page', name: 'Landing Page' },
                { slug: 'lead-form', name: 'Lead Capture' },
            ]
        },
        companyLinks() {
            return [
                { id: 'home', label: 'Home', href: this.route('home') },
                { id: 'blog', label: 'Blog', href: this.route('blog') },
                { id: 'contact', label: 'Contact', href: this.route('contact') },
                { id: 'login', label: 'Login', href: this.route('login') },
            ]
        },
    },
    methods: {
        subscribe() {
            this.form.post(this.route('subscribe.news'), {
                onSuccess: () => this.form.reset('email'),
            })
        },
    },
}
</script>

<style scoped>
.sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
}
</style>
